/**
 * The recut Colour section (M15-UI-01, D114/D115): profile select +
 * Edit profiles…, the (edited)-copy state with Update / Save as new /
 * Revert, count + minimum distance, Must-use chips with search-to-add,
 * the conflicts list, and the My-threads inventory reveal (ownership
 * is the inventory's own concern and survives the cutover here).
 *
 * The section fills the slot M14-EXT-42 protected. Controls build
 * once; update() lands values in place, with EXT-43 fingerprints
 * gating every rebuildable region — the palette-panel contract,
 * carried by its successor.
 */

import { renderProfileOptions } from './profile-options.ts';
import type { PaletteConflict } from '../core/palette-policy.ts';
import { createBrowseTable, type BrowseRow, type BrowseTable } from './browse-table.ts';
import { toggleField } from './controls.ts';

/** One profile as the select lists it. */
export interface SectionProfile {
  id: string;
  name: string;
  builtin: boolean;
  /** Menu group label (MENU-01); omitted renders the option ungrouped. */
  group?: string | undefined;
}

/** The section's view of the design's colour state. */
export interface ColourSectionState {
  paletteMode: boolean;
  profiles: SectionProfile[];
  /** The linked profile id, or null when the design stands alone. */
  profileRef: string | null;
  /** True when the design's copy differs from the linked profile. */
  edited: boolean;
  count: { mode: 'all' | 'max' | 'exact'; n: number };
  minDistance: number;
  mustUse: string[];
  /**
   * The design's colour swaps (ICE-RECOLOUR-01, D182), already
   * labelled by the host. A dangling swap — `from` no longer in the
   * palette — is kept and said so (the D178 drift rule), never
   * silently dropped.
   */
  swaps: SwapChip[];
  conflicts: PaletteConflict[];
  eligibleCount: number;
  /**
   * True while the inventory has no threads in this browser: the
   * "My inventory" profile is then empty by construction, and the
   * select says so instead of offering it (MYTHREADS-01).
   */
  inventoryEmpty: boolean;
}

/** One swap as the chip list shows it. */
export interface SwapChip {
  /** The swapped entry's id — the key `removeSwap` takes. */
  from: string;
  fromLabel: string;
  toLabel: string;
  dangling: boolean;
}

/** Everything the section calls back into the host for. */
export interface ColourSectionActions {
  setPaletteMode(on: boolean): void;
  /** Adopt a profile: the design takes a fresh copy of its recipe. */
  selectProfile(id: string): void;
  updateProfile(): void;
  saveAsNew(): void;
  revert(): void;
  setCount(mode: 'all' | 'max', n: number): void;
  setMinDistance(value: number): void;
  addMustUse(id: string): void;
  removeMustUse(id: string): void;
  /** Drop the swap rule for this entry (Remove is the undo — D182). */
  removeSwap(from: string): void;
  openEditor(): void;
  /** Row model for the Must-use add search (the whole universe). */
  browseRows(query: string): { rows: BrowseRow[]; total: number };
  /** Label for a must-use id (falls back to the id). */
  labelFor(id: string): string;
  /**
   * Fill the My-threads inventory reveal. Ownership is the
   * inventory's own concern (UI-03 rule) and survives the cutover
   * here — the host owns the plumbing, this section owns the slot.
   */
  mountInventory(container: HTMLElement): void;
  /**
   * Fill the tone-matching slot (TONE-01) — the same host-owns-the-
   * plumbing pattern as the inventory: the section owns where the
   * group sits (after the selection rules, before Must-use) and its
   * visibility with the mode; the tone controls own everything inside.
   */
  mountTone(container: HTMLElement): void;
}

export interface ColourSection {
  element: HTMLElement;
  update(state: ColourSectionState): void;
  /** Open the inventory reveal and focus its search — the preview
   *  banner's "Add threads" route (MYTHREADS-01). */
  openInventory(): void;
}

/** Sentinel select value for a design linked to no profile — the
 *  never-lying state the dither select established (review of D124,
 *  punch item 2): a migrated old file must not wear a built-in's
 *  name. */
export const UNLINKED_DESIGN = 'custom:design';

/** The built-in whose membership is the browser's inventory. */
export const MY_INVENTORY_PROFILE = 'builtin:my-threads';

/**
 * The colour-limit slider's scale (ICE-LIMIT-01, signed at D188):
 * floor 2, ceiling 512, logarithmic, with the **midpoint at 16** —
 * two log segments meeting there, so the half of the travel most
 * designs live in (2–16) is as wide as the half that reaches the
 * ceiling. The slider's own value is a position in `0..steps`; the
 * stored count stays `n`, so old projects load unchanged. 300 steps
 * keep every whole count below ~40 one position apart and an arrow
 * key meaningful (≈ 1.4 % per step low, ≈ 2.3 % high); the number
 * input beside the slider is the exact handle.
 */
export const COUNT_SCALE = { floor: 2, mid: 16, ceiling: 512, steps: 300 } as const;

/** Slider position (0..steps) → colour count, rounded to a whole colour. */
export function sliderToCount(position: number): number {
  const { floor, mid, ceiling, steps } = COUNT_SCALE;
  const p = Math.min(1, Math.max(0, position / steps));
  const n =
    p <= 0.5
      ? floor * Math.pow(mid / floor, p / 0.5)
      : mid * Math.pow(ceiling / mid, (p - 0.5) / 0.5);
  return Math.min(ceiling, Math.max(floor, Math.round(n)));
}

/** Colour count → slider position (0..steps); counts outside the scale clamp. */
export function countToSlider(n: number): number {
  const { floor, mid, ceiling, steps } = COUNT_SCALE;
  const c = Math.min(ceiling, Math.max(floor, n));
  const p =
    c <= mid
      ? 0.5 * (Math.log(c / floor) / Math.log(mid / floor))
      : 0.5 + 0.5 * (Math.log(c / mid) / Math.log(ceiling / mid));
  return Math.round(p * steps);
}

/** Build the recut Colour section. */
export function createColourSection(
  doc: Document,
  initial: ColourSectionState,
  actions: ColourSectionActions,
): ColourSection {
  let state = initial;
  const element = doc.createElement('fieldset');

  // --- mode ---------------------------------------------------------
  const threadify = toggleField(doc, 'threadify-colours', 'Threadify colours', initial.paletteMode, (on) => {
    actions.setPaletteMode(on);
  });

  // --- profile select + editor entry -------------------------------
  const profileField = doc.createElement('div');
  profileField.className = 'field';
  const profileLabel = doc.createElement('label');
  profileLabel.htmlFor = 'colour-profile';
  profileLabel.textContent = 'Colour profile';
  const profileSelect = doc.createElement('select');
  profileSelect.id = 'colour-profile';
  profileSelect.addEventListener('change', () => {
    // Picking the sentinel is a no-op: it names the current state,
    // it is not an adoptable profile.
    if (profileSelect.value === UNLINKED_DESIGN) return;
    actions.selectProfile(profileSelect.value);
  });
  profileField.append(profileLabel, profileSelect);
  const editButton = doc.createElement('button');
  editButton.type = 'button';
  editButton.textContent = 'Edit profiles…';
  editButton.addEventListener('click', () => {
    actions.openEditor();
  });
  const profileRow = doc.createElement('div');
  profileRow.className = 'toolbar profile-row';
  profileRow.append(profileField, editButton);

  // --- the (edited) actions ----------------------------------------
  const editedRow = doc.createElement('div');
  editedRow.className = 'toolbar edited-row';
  editedRow.hidden = true;
  const editedNote = doc.createElement('p');
  editedNote.className = 'meta';
  editedNote.textContent = 'This design has its own edits to the profile.';
  const builtinReason = doc.createElement('p');
  builtinReason.className = 'helper';
  builtinReason.id = 'update-profile-reason';
  builtinReason.textContent =
    'The linked profile is built-in and read-only — Save as new keeps these edits.';
  builtinReason.hidden = true;
  const verb = (text: string, onClick: () => void): HTMLButtonElement => {
    const b = doc.createElement('button');
    b.type = 'button';
    b.textContent = text;
    b.addEventListener('click', onClick);
    return b;
  };
  const updateButton = verb('Update profile', () => {
    actions.updateProfile();
  });
  updateButton.setAttribute('aria-describedby', builtinReason.id);
  const saveAsNewButton = verb('Save as new', () => {
    actions.saveAsNew();
  });
  const revertButton = verb('Revert', () => {
    actions.revert();
  });
  editedRow.append(editedNote, updateButton, saveAsNewButton, revertButton, builtinReason);

  // --- count + minimum distance ------------------------------------
  const limitToggle = toggleField(
    doc,
    'limit-colours',
    'Constrain number of colours',
    initial.count.mode !== 'all',
    (on) => {
      actions.setCount(on ? 'max' : 'all', state.count.n);
    },
  );
  const countCluster = doc.createElement('div');
  countCluster.className = 'count-cluster';
  const countWrap = doc.createElement('div');
  countWrap.className = 'field';
  const rangeLabel = doc.createElement('label');
  rangeLabel.htmlFor = 'count-range';
  rangeLabel.textContent = 'Colours';
  const countRange = doc.createElement('input');
  countRange.type = 'range';
  countRange.id = 'count-range';
  // A position on the log scale, not the count (ICE-LIMIT-01); the
  // number input beside it is the count itself, so the pair reads as
  // one control with a coarse and a fine handle. The range's own
  // `aria-valuetext` speaks the count, never the position.
  countRange.min = '0';
  countRange.max = String(COUNT_SCALE.steps);
  countRange.step = '1';
  countRange.addEventListener('input', () => {
    const n = sliderToCount(Number(countRange.value));
    countRange.setAttribute('aria-valuetext', `${String(n)} colours`);
    // Many positions round to one count at the low end; a re-select
    // and reprocess per position would be paid for nothing.
    if (n === state.count.n && state.count.mode !== 'all') return;
    actions.setCount('max', n);
  });
  countWrap.append(rangeLabel, countRange);
  const countNumWrap = doc.createElement('div');
  countNumWrap.className = 'field';
  const countNumLabel = doc.createElement('label');
  countNumLabel.htmlFor = 'count-n';
  countNumLabel.textContent = 'Number of colours';
  const countInput = doc.createElement('input');
  countInput.type = 'number';
  countInput.id = 'count-n';
  countInput.min = '1';
  countInput.max = '512';
  countInput.step = '1';
  countInput.addEventListener('change', () => {
    const n = Math.max(1, Math.min(512, Math.round(Number(countInput.value) || 1)));
    countInput.value = String(n);
    actions.setCount('max', n);
  });
  countNumWrap.append(countNumLabel, countInput);
  const countHelper = doc.createElement('p');
  countHelper.className = 'helper';
  countHelper.id = 'count-n-helper';
  countHelper.textContent = 'The slider runs from 2 to 512, finest below 16; type an exact number here.';
  countInput.setAttribute('aria-describedby', countHelper.id);
  countCluster.append(countWrap, countNumWrap, countHelper);

  const distanceField = doc.createElement('div');
  distanceField.className = 'field';
  const distanceLabel = doc.createElement('label');
  distanceLabel.htmlFor = 'min-distance';
  distanceLabel.textContent = 'Minimum distance';
  const distanceRow = doc.createElement('div');
  distanceRow.className = 'stitch-size-row';
  const distanceRange = doc.createElement('input');
  distanceRange.type = 'range';
  distanceRange.id = 'min-distance';
  distanceRange.min = '0';
  distanceRange.max = '40';
  distanceRange.step = '1';
  const distanceValue = doc.createElement('span');
  distanceValue.className = 'meta';
  const distanceHelper = doc.createElement('p');
  distanceHelper.className = 'helper';
  distanceHelper.id = 'min-distance-helper';
  distanceHelper.textContent =
    'Keeps chosen colours at least this far apart (ΔE) — 0 is off; Must-use colours are always kept.';
  distanceRange.setAttribute('aria-describedby', distanceHelper.id);
  distanceRange.addEventListener('input', () => {
    actions.setMinDistance(Number(distanceRange.value));
  });
  distanceRow.append(distanceRange, distanceValue);
  distanceField.append(distanceLabel, distanceRow, distanceHelper);

  // --- tone matching slot (TONE-01) ---------------------------------
  const toneSlot = doc.createElement('div');
  toneSlot.className = 'tone-slot';
  actions.mountTone(toneSlot);

  // --- Must-use chips + search-to-add ------------------------------
  const mustUseLabel = doc.createElement('p');
  mustUseLabel.className = 'group-label';
  mustUseLabel.textContent = 'Must-use colours';
  // Seat, not presence (MUST-01): the promise is a place in the
  // palette, and the stitches still go to their nearest colour — the
  // half of "must use" the live-app reporter read the other way. The
  // last sentence is the consequence of a pick outside the profile: it
  // widens this design's copy, which the select then shows as edited.
  const mustUseHelper = doc.createElement('p');
  mustUseHelper.className = 'helper';
  mustUseHelper.id = 'must-use-helper';
  mustUseHelper.textContent =
    'Guaranteed a place in the palette; stitches still go to their nearest colour, so a seat can go unused. A colour from outside the profile joins this design’s colours.';
  const chipList = doc.createElement('div');
  chipList.className = 'pin-chips';
  chipList.setAttribute('role', 'list');
  const addDetails = doc.createElement('details');
  addDetails.className = 'depth-reveal';
  const addSummary = doc.createElement('summary');
  addSummary.textContent = 'Add a must-use colour';
  const addBody = doc.createElement('div');
  addBody.className = 'depth-reveal-body';
  addDetails.append(addSummary, addBody);

  // --- Swaps (ICE-RECOLOUR-01) --------------------------------------
  // Presence, where Must-use is a seat: a swap says which thread the
  // stitches the mapper gave X are worked in. Made from the Colours
  // used table's Swap… verb; this list is the rule's second home, the
  // one place a dangling swap can still be seen and removed.
  const swapsLabel = doc.createElement('p');
  swapsLabel.className = 'group-label';
  swapsLabel.textContent = 'Swaps';
  const swapsHelper = doc.createElement('p');
  swapsHelper.className = 'helper';
  swapsHelper.id = 'swaps-helper';
  swapsHelper.textContent =
    'Stitches the mapper gives one colour are worked in another. Swap a colour from the Colours used table; a swap whose colour leaves the palette is kept until you remove it.';
  const swapChips = doc.createElement('div');
  swapChips.className = 'pin-chips';
  swapChips.setAttribute('role', 'list');

  // --- conflicts (the never-silent surface) ------------------------
  const summary = doc.createElement('p');
  summary.className = 'meta';
  summary.id = 'palette-summary';
  const conflictList = doc.createElement('ul');
  conflictList.className = 'conflicts';
  conflictList.setAttribute('aria-live', 'polite');

  // --- My inventory reveal -----------------------------------------
  const inventoryDetails = doc.createElement('details');
  inventoryDetails.className = 'depth-reveal';
  const inventorySummary = doc.createElement('summary');
  inventorySummary.textContent = 'My inventory';
  const inventoryBody = doc.createElement('div');
  inventoryBody.className = 'depth-reveal-body';
  inventoryDetails.append(inventorySummary, inventoryBody);
  actions.mountInventory(inventoryBody);

  element.append(
    threadify.element,
    profileRow,
    editedRow,
    limitToggle.element,
    countCluster,
    distanceField,
    toneSlot,
    mustUseLabel,
    mustUseHelper,
    chipList,
    addDetails,
    swapsLabel,
    swapsHelper,
    swapChips,
    summary,
    conflictList,
    inventoryDetails,
  );

  // Build the add-search once (it queries live through the action).
  const mustUseTable: BrowseTable = createBrowseTable(doc, {
    searchId: 'must-use-search',
    searchLabel: 'Find a colour to guarantee',
    rowsFor: (query) => actions.browseRows(query),
    rowActions: (row) => {
      const add = doc.createElement('button');
      add.type = 'button';
      add.textContent = 'Must use';
      add.setAttribute('aria-label', `Must use ${row.label}`);
      add.setAttribute('aria-pressed', String(state.mustUse.includes(row.id)));
      add.addEventListener('click', () => {
        if (state.mustUse.includes(row.id)) actions.removeMustUse(row.id);
        else actions.addMustUse(row.id);
        mustUseTable.refresh();
      });
      return [add];
    },
    emptyText: 'Nothing here yet.',
  });
  addBody.append(mustUseTable.element);

  let lastOptionsFp = '';
  let lastChipsFp = '';
  let lastSwapsFp = '';
  let lastConflictsFp = '';

  function update(next: ColourSectionState): void {
    state = next;
    const on = next.paletteMode;
    threadify.input.checked = on;
    const threadifyState = threadify.input.nextElementSibling;
    if (threadifyState !== null) threadifyState.textContent = on ? 'On' : 'Off';
    for (const el of [
      profileRow,
      editedRow,
      limitToggle.element,
      countCluster,
      distanceField,
      toneSlot,
      mustUseLabel,
      mustUseHelper,
      chipList,
      addDetails,
      swapsLabel,
      swapsHelper,
      swapChips,
      summary,
      conflictList,
    ]) {
      el.hidden = !on;
    }
    if (!on) return;

    // Profile select: options rebuild only when the list moved; the
    // (edited) suffix rides the linked option's label (EXT-43).
    const optionsFp = JSON.stringify([
      next.profiles.map((p) => [p.id, p.name, p.builtin, p.group]),
      next.profileRef,
      next.edited,
      next.inventoryEmpty,
    ]);
    if (optionsFp !== lastOptionsFp) {
      lastOptionsFp = optionsFp;
      renderProfileOptions(doc, profileSelect, {
        // An unlinked design (a migrated old file, or a deleted
        // profile's orphan) names itself honestly rather than
        // wearing the first option's name. It is not a profile, so it
        // sits above every group rather than inside one.
        leading:
          next.profileRef === null
            ? { value: UNLINKED_DESIGN, label: 'This design’s colours' }
            : undefined,
        items: next.profiles.map((profile) => {
          // "(built-in)" retired here at MENU-01: the groups carry
          // that now, and repeating it on 25 of the options was
          // duplicated noise. "(edited)" stays — it says something no
          // group can (EXT-43).
          let label =
            profile.id === next.profileRef && next.edited
              ? `${profile.name} (edited)`
              : profile.name;
          let disabled = false;
          // An empty inventory makes "My inventory" a dead end
          // (MYTHREADS-01): a fresh pick is refused with the reason in
          // the label; a design already linked to it (a loaded file)
          // keeps its option selectable, labelled, and the preview
          // banner offers the way out.
          if (profile.id === MY_INVENTORY_PROFILE && next.inventoryEmpty) {
            if (profile.id === next.profileRef) {
              label += ' — empty in this browser';
            } else {
              disabled = true;
              label += ' — empty: mark threads as owned first';
            }
          }
          return { id: profile.id, label, group: profile.group, disabled };
        }),
        value: next.profileRef ?? UNLINKED_DESIGN,
      });
    }
    editedRow.hidden = !next.edited;
    // Updating a built-in in place is impossible — the store refuses;
    // Save as new is the route (Update disabled, reason in its title).
    const builtinLinked =
      next.profiles.find((p) => p.id === next.profileRef)?.builtin ?? false;
    updateButton.disabled = builtinLinked;
    // The reason is a visible sentence, not a hover-only title
    // (review of D124, punch item 5): disabled controls still owe
    // their explanation to keyboard and AT users.
    builtinReason.hidden = !(builtinLinked && next.edited);

    const limited = next.count.mode !== 'all';
    limitToggle.input.checked = limited;
    const limitState = limitToggle.input.nextElementSibling;
    if (limitState !== null) limitState.textContent = limited ? 'On' : 'Off';
    countCluster.hidden = !limited;
    if (doc.activeElement !== countRange) {
      countRange.value = String(countToSlider(next.count.n));
      countRange.setAttribute('aria-valuetext', `${String(next.count.n)} colours`);
    }
    if (doc.activeElement !== countInput) countInput.value = String(next.count.n);
    if (doc.activeElement !== distanceRange) {
      distanceRange.value = String(next.minDistance);
    }
    distanceValue.textContent = next.minDistance === 0 ? 'off' : `ΔE ${String(next.minDistance)}`;

    const chipsFp = next.mustUse.join('\n');
    if (chipsFp !== lastChipsFp) {
      lastChipsFp = chipsFp;
      chipList.replaceChildren();
      for (const id of next.mustUse) {
        const chip = doc.createElement('span');
        chip.className = 'pin-chip';
        chip.setAttribute('role', 'listitem');
        const text = doc.createElement('span');
        text.textContent = actions.labelFor(id);
        const remove = doc.createElement('button');
        remove.type = 'button';
        remove.textContent = 'Remove';
        remove.setAttribute('aria-label', `Remove must-use ${actions.labelFor(id)}`);
        remove.addEventListener('click', () => {
          actions.removeMustUse(id);
        });
        chip.append(text, remove);
        chipList.append(chip);
      }
      chipList.hidden = next.mustUse.length === 0;
    }

    const swapsFp = next.swaps
      .map((s) => [s.from, s.fromLabel, s.toLabel, s.dangling].join('\u0000'))
      .join('\n');
    if (swapsFp !== lastSwapsFp) {
      lastSwapsFp = swapsFp;
      swapChips.replaceChildren();
      for (const swap of next.swaps) {
        const chip = doc.createElement('span');
        chip.className = 'pin-chip';
        chip.setAttribute('role', 'listitem');
        const text = doc.createElement('span');
        // The arrow reads "right arrow" to AT — close enough; the
        // button's accessible name says it in words.
        text.textContent = swap.dangling
          ? `${swap.fromLabel} → ${swap.toLabel} (${swap.fromLabel} is not in the palette now — kept)`
          : `${swap.fromLabel} → ${swap.toLabel}`;
        const remove = doc.createElement('button');
        remove.type = 'button';
        remove.textContent = 'Remove';
        remove.setAttribute(
          'aria-label',
          `Remove the swap of ${swap.fromLabel} for ${swap.toLabel}`,
        );
        remove.addEventListener('click', () => {
          actions.removeSwap(swap.from);
        });
        chip.append(text, remove);
        swapChips.append(chip);
      }
    }
    // The group shows only once a swap exists: an empty "Swaps"
    // heading over nothing is the blank-panel anti-pattern, and the
    // verb that creates one lives in the table below. Re-applied every
    // update because the mode loop above unhides the group with the
    // rest of the section.
    const noSwaps = next.swaps.length === 0;
    swapsLabel.hidden = noSwaps;
    swapsHelper.hidden = noSwaps;
    swapChips.hidden = noSwaps;

    // The consequence beside the cause (COUNT-01): with no palette the
    // preview shows the picture as it is, and the count and Must-use
    // controls above this line are not in force — the "Problem:" line
    // below says why, this says what that means.
    summary.textContent =
      next.eligibleCount === 0
        ? '0 colours available — the picture is shown without threads, and the colour limit and Must-use colours do not apply until this profile resolves.'
        : `${String(next.eligibleCount)} colour${next.eligibleCount === 1 ? '' : 's'} available.`;

    const conflictsFp = JSON.stringify(next.conflicts.map((c) => [c.severity, c.message]));
    if (conflictsFp !== lastConflictsFp) {
      lastConflictsFp = conflictsFp;
      conflictList.replaceChildren();
      for (const conflict of next.conflicts) {
        const item = doc.createElement('li');
        item.className = conflict.severity === 'error' ? 'conflict-error' : 'conflict-warning';
        item.textContent = `${conflict.severity === 'error' ? 'Problem' : 'Note'}: ${conflict.message}`;
        conflictList.append(item);
      }
    }
  }

  function openInventory(): void {
    inventoryDetails.open = true;
    inventoryBody.querySelector<HTMLInputElement>('#inventory-search')?.focus();
  }

  update(initial);
  return { element, update, openInventory };
}
